import axios from "axios";
import { API } from "common";
import { IMMUTABLE_LICENSES_PROPERTIES } from "core/constants";
import { objectWithFilteredKeys } from "common/utilities";

// Mock API instance - change to API when backend is ready
const MOCK_API = axios.create({
  baseURL: import.meta.env.REACT_APP_MOCK_SERVER_URL || "http://localhost:3100",
});
const transformAxiosResponse = (response) => {
  if (response.data) {
    if (response.data.data) {
      const { data, ...rest } = response.data;
      return {
        rows: data,
        ...rest,
      };
    }
    return response.data;
  }

  return response;
};
MOCK_API.interceptors.response.use(transformAxiosResponse);

const removeUndefinedParams = (params) => {
  const definedEntries = Object.entries(params).filter(
    ([key, value]) => value !== undefined && value !== ""
  );
  const onlyDefinedParamsObj = Object.fromEntries(definedEntries);

  return onlyDefinedParamsObj;
};

export const getOrganizationById = ({
  organizationId,
  include,
  accessToken,
  signal,
}) => {
  return API.get(`/organization/${organizationId}`, {
    params: {
      is_membership_endpoint: true,
      include: include || null,
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    signal,
  });
};

export const getOrganizations = ({ include, accessToken, signal }) => {
  return API.get("/organization", {
    params: {
      is_membership_endpoint: true,
      limit: 0,
      include: include || null,
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    signal,
  });
};

export const getOrganizationOnboarding = ({
  organizationId,
  accessToken,
  signal,
}) => {
  return API.get(`/organization/${organizationId}/onboarding`, {
    params: {
      is_membership_endpoint: true,
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    signal,
  });
};

export const getOrganizationDeployment = ({
  organizationId,
  accessToken,
  signal,
}) => {
  return API.get(`/organization/${organizationId}/deployment`, {
    params: {
      is_membership_endpoint: true,
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    signal,
  });
};

export const createOrganization = ({
  id,
  name,
  domain,
  primaryContact,
  accessToken,
  customer_segment,
  medicare_licensed,
}) => {
  return API.post(
    "/organization",
    {
      id,
      name,
      domain,
      primaryContact,
      customer_segment,
      medicare_licensed,
    },
    {
      params: {
        is_membership_endpoint: true,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

export const updateOrganization = ({
  accessToken,
  organizationId,
  ...requestBody
}) => {
  return API.put(
    `/organization`,
    {
      ...requestBody,
    },
    {
      params: {
        is_membership_endpoint: true,
        org_id: organizationId,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

export const setupOrganization = ({ organizationId, accessToken }) => {
  return API.post(
    `/organization/${organizationId}/onboarding`,
    {},
    {
      params: {
        is_membership_endpoint: true,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

export const deployLicense = ({ organizationId, accessToken }) => {
  return API.post(
    `/organization/${organizationId}/deployment`,
    {},
    {
      params: { is_membership_endpoint: true },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

export const updateOrganizationLicenses = ({
  organizationId,
  accessToken,
  licenses,
}) => {
  return API.put(
    `/licenses`,
    {
      licenses: objectWithFilteredKeys(licenses, IMMUTABLE_LICENSES_PROPERTIES),
    },
    {
      params: {
        is_membership_endpoint: true,
        org_id: organizationId,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

export const switchCustomerSegment = ({
  organizationId,
  accessToken,
  customer_segment,
  medicare_licensed,
  licenses,
}) => {
  if (licenses) {
    return API.post(
      `/organization/${organizationId}/segment-switch`,
      {
        customer_segment,
        medicare_licensed,
        licenses: objectWithFilteredKeys(
          licenses,
          IMMUTABLE_LICENSES_PROPERTIES
        ),
      },
      {
        params: {
          is_membership_endpoint: true,
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
  } else {
    return API.post(
      `/organization/${organizationId}/segment-switch`,
      {
        customer_segment,
        medicare_licensed,
      },
      {
        params: {
          is_membership_endpoint: true,
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
  }
};

export const deactivateOrganization = (params) => {
  const { organizationId, accessToken } = params;
  return API.post(
    `/organization/${organizationId}/offboarding`,
    {},
    {
      params: {
        is_membership_endpoint: true,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

export const activateOrganization = (params) => {
  const { organizationId, accessToken } = params;
  return API.post(
    `/organization/${organizationId}/activation`,
    {
      active: true,
    },
    {
      params: {
        is_membership_endpoint: true,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

export const getProductCatalog = ({
  accessToken,
  include,
  filters,
  isPrCatalog = false,
  signal,
}) => {
  return API.get("/catalog/products", {
    params: {
      include: include.join(","),
      filters: JSON.stringify(filters),
      ...(isPrCatalog ? { pr_catalog: true } : {}),
      is_membership_endpoint: true,
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    signal,
  });
};

export const getProductCatalogVersion = ({
  accessToken,
  isPrCatalog = false,
  signal,
}) => {
  return API.get("/catalog/products/version", {
    params: {
      ...(isPrCatalog ? { pr_catalog: true } : {}),
      is_membership_endpoint: true,
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    signal,
  });
};

export const generateUserDataAccessCredential = (params) => {
  const { accessToken, userId, organizationId } = params;

  return API.post(
    `/user-profiles/${userId}/credential`,
    {},
    {
      params: {
        is_membership_endpoint: true,
        org_id: organizationId,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

export const generateOrgDataAccessCredential = (params) => {
  const { accessToken, organizationId } = params;

  return API.post(
    `/organization/${organizationId}/credential`,
    {},
    {
      params: {
        is_membership_endpoint: true,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

export const disableOrganizationCredentialById = (params) => {
  const { accessToken, organizationId, credentialId } = params;

  return API.put(
    `/organization/${organizationId}/credential/${credentialId}`,
    { credential_enabled: false },
    {
      params: {
        is_membership_endpoint: true,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

export const disableIndividualCredentialById = (params) => {
  const { accessToken, userId, credentialId, organizationId } = params;

  return API.put(
    `/user-profiles/${userId}/credential/${credentialId}`,
    { credential_enabled: false },
    {
      params: {
        is_membership_endpoint: true,
        org_id: organizationId,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

export const getCustomerSegments = ({ accessToken, filters, signal }) => {
  return API.get("/catalog/customer-segments", {
    params: {
      filters: JSON.stringify(filters),
      is_membership_endpoint: true,
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    signal,
  });
};

export const getUserProfiles = ({
  accessToken,
  email,
  enabledStatus,
  organizationId,
  limit = 0,
  page_token,
  sort,
}) => {
  return API.get("/user-profiles", {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    params: removeUndefinedParams({
      email,
      enabled: enabledStatus,
      org_id: organizationId,
      include: "memberships",
      is_membership_endpoint: true,
      limit,
      //   next_token,
      //   sort,
    }),
  });
};

export const getUserProfileById = async ({ accessToken, userId }) => {
  return API.get(`/user-profiles/${userId}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    params: { include: "memberships", is_membership_endpoint: true },
  });
};

export const createUserProfiles = ({ accessToken, profiles = [] }) => {
  return API.post("/user-profiles", profiles, {
    params: {
      is_membership_endpoint: true,
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

export const getAssignableOrganizationsByEmail = ({ accessToken, email }) => {
  return API.get(`/memberships/eligibility`, {
    params: {
      email,
      is_membership_endpoint: true,
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

export const updateUserProfileByUserId = ({ accessToken, userId, profile }) => {
  return API.patch(`/user-profiles/${userId}`, profile, {
    params: {
      is_membership_endpoint: true,
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

export const deleteUserProfile = ({ userId, accessToken }) => {
  return API.delete(`/user-profiles/${userId}`, {
    params: {
      is_membership_endpoint: true,
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

export const getMemberships = ({ accessToken, userId, orgId }) => {
  return API.get("/memberships", {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    params: removeUndefinedParams({
      user_id: userId,
      org_id: orgId,
      include: "profile",
      is_membership_endpoint: true,
      limit: 0,
    }),
  });
};

export const createMemberships = ({ accessToken, memberships = [] }) => {
  return API.post("/memberships", memberships, {
    params: {
      is_membership_endpoint: true,
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

export const updateMemberships = ({ accessToken, memberships = [] }) => {
  return API.patch("/memberships", memberships, {
    params: {
      is_membership_endpoint: true,
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

export const deleteMemberships = ({ accessToken, memberships = [] }) => {
  return API.post("/memberships/delete", memberships, {
    params: {
      is_membership_endpoint: true,
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

export const loginToAbsorb = (params) => {
  const { accessToken } = params;

  return API.post(
    `/absorb/login`,
    {},
    {
      params: {
        is_membership_endpoint: true,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

/**
 *
 * Analytics Hub Endpoints
 */
export const getStandardWorkbooks = async ({
  organizationId,
  accessToken,
  signal,
}) => {
  return API.get(`/organization/${organizationId}/bix/workbooks/standard`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    signal,
  });
};

export const getCustomWorkbooks = async ({
  organizationId,
  accessToken,
  signal,
}) => {
  const response = await API.get(
    `/organization/${organizationId}/bix/workbooks/custom`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      signal,
    }
  );
  return response;
};

export const getWorkbookEmbedUrl = async ({
  organizationId,
  workbookUrlId,
  workbookCatalogId,
  version,
  accessToken,
  signal,
}) => {
  const response = await API.post(
    `/organization/${organizationId}/bix/embed`,
    {
      workbook: {
        identifier: removeUndefinedParams({
          workbook_url_id: workbookUrlId ?? "",
          workbook_catalog_id: workbookCatalogId ?? "",
        }),
        version: version,
      },
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      signal,
    }
  );
  return response;
};

/**
 *
 * Analytics Hub Saved View Endpoints
 *
 * A saved view stores a `sigma_explore_key` — the Sigma exploration state token
 * emitted by the embed's `workbook:exploreKey:onchange` event — under a
 * user-defined name, scoped to a single workbook.
 */

// Every saved view the caller can see, scoped to a workbook and version.
// `limit: 0` asks for the whole set — the picker lists them all.
export const getSavedViews = ({
  organizationId,
  workbookUrlId,
  version,
  limit = 0,
  accessToken,
  signal,
}) => {
  return API.get(`/organization/${organizationId}/bix/saved-views`, {
    params: removeUndefinedParams({
      workbook_url_id: workbookUrlId,
      version,
      limit,
    }),
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    signal,
  });
};

// `version` (alpha | beta | live) is required by the API even though it is
// absent from the published request schema.
export const createSavedView = ({
  organizationId,
  name,
  sigmaExploreKey,
  isPublic,
  workbookUrlId,
  version,
  accessToken,
  signal,
}) => {
  return API.post(
    `/organization/${organizationId}/bix/saved-views`,
    {
      name,
      sigma_explore_key: sigmaExploreKey,
      is_public: isPublic,
      workbook_url_id: workbookUrlId,
      version,
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      signal,
    }
  );
};

// Partial update; the API needs one field and 403s anyone but the creator.
// `version` always rides along — names are unique within it.
export const updateSavedView = ({
  organizationId,
  savedViewId,
  name,
  sigmaExploreKey,
  isPublic,
  version,
  accessToken,
  signal,
}) => {
  return API.patch(
    `/organization/${organizationId}/bix/saved-views/${savedViewId}`,
    removeUndefinedParams({
      name,
      sigma_explore_key: sigmaExploreKey,
      is_public: isPublic,
      version,
    }),
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      signal,
    }
  );
};

// Permanent, no restore — confirm with the user first. Allowed for the creator,
// and for public views also admins; the API 403s anyone else.
export const deleteSavedView = ({
  organizationId,
  savedViewId,
  accessToken,
  signal,
}) => {
  return API.delete(
    `/organization/${organizationId}/bix/saved-views/${savedViewId}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      signal,
    }
  );
};
